import React, { useState, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import AiSupportChatModal from './AiSupportChatModal';
import {
  MessageSquare,
  Phone,
  Mail,
  ChevronDown,
  ChevronRight,
  Clock,
  ShieldCheck,
  Sparkles,
  HelpCircle
} from 'lucide-react';

const WHATSAPP_NUMBER = '919929371541';
const CALL_NUMBER = '9166277283';
const SUPPORT_EMAIL = 'support@moxpal.com';

const HelpSupportHome = ({ userProfile }) => {
  const { t } = useLang();
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      id: 1,
      q: t(LangKeys.help_faq_1_q) || 'खाता में नया ग्राहक या सप्लायर कैसे जोड़ें?',
      a: t(LangKeys.help_faq_1_a) || 'होम स्क्रीन पर "Add Customer" बटन दबाएं, नाम और मोबाइल नंबर दर्ज करें और सुरक्षित करें।'
    },
    {
      id: 2,
      q: t(LangKeys.help_faq_2_q) || 'डेयरी पासबुक में दूध कलेक्शन कैसे करें?',
      a: t(LangKeys.help_faq_2_a) || 'डेयरी सेक्शन में जाकर "दूध एंट्री" चुनें, फैट/SNF या सीधे रेट दर्ज करके 1-क्लिक में रसीद भेजें।'
    },
    {
      id: 3,
      q: t(LangKeys.help_faq_3_q) || 'डेटा बैकअप कैसे सुरक्षित रहता है?',
      a: t(LangKeys.help_faq_3_a) || 'मोक्षपाल खाता पर आपका समस्त डेटा क्लाउड सर्वर पर 100% एन्क्रिप्टेड और सुरक्षित रहता है।'
    },
    {
      id: 4,
      q: t(LangKeys.help_faq_4_q) || 'सब्सक्रिप्शन प्लान कैसे अपग्रेड करें?',
      a: t(LangKeys.help_faq_4_a) || 'सेटिंग्स में जाकर "मेरी सदस्यता" पर जाएं और अपनी पसंद का प्लान चुनकर सुरक्षित पेमेंट करें।'
    }
  ];

  const handleLaunchWhatsApp = () => {
    const text = encodeURIComponent('नमस्ते Moxpal Support Team, मुझे Moxpal Khata ऐप में सहायता चाहिए।');
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank');
  };

  const handleLaunchCall = () => {
    window.open(`tel:${CALL_NUMBER}`, '_self');
  };

  const handleLaunchEmail = () => {
    window.open(`mailto:${SUPPORT_EMAIL}?subject=Moxpal%20Support%20Query`, '_self');
  };

  return (
    <div style={styles.container}>
      {/* 1. DARK GRADIENT HELP CENTER BANNER (Matching Flutter App) */}
      <div style={styles.banner}>
        <div style={styles.bannerIconWrapper}>
          <div style={styles.bannerIconInner}>
            <img
              src="/logo.png"
              alt="Moxpal"
              style={{ width: '38px', height: '38px', objectFit: 'contain', display: 'block' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
        <h2 style={styles.bannerTitle}>
          {t(LangKeys.help_center_heading) || 'Moxpal Khata Help Center'}
        </h2>
        <p style={styles.bannerSubtitle}>
          {t(LangKeys.help_center_subheading) || 'FAQs, ट्यूटोरियल और कस्टमर सपोर्ट के लिए आपका वन-स्टॉप सेंटर'}
        </p>
      </div>

      <div style={styles.content}>
        {/* 2. 24x7 AI ASSISTANT CARD (With Real-Time Chat Action) */}
        <div style={styles.aiCard} onClick={() => setIsAiChatOpen(true)}>
          <div style={styles.aiCardTop}>
            <div style={styles.aiLogoBox}>
              <img
                src="/logo.png"
                alt="AI"
                style={{ width: '32px', height: '32px', objectFit: 'contain' }}
              />
            </div>
            <div style={styles.aiTextCol}>
              <div style={styles.aiTitleRow}>
                <span style={styles.aiTitle}>{t(LangKeys.help_ai_title) || 'Moxpal AI Support'}</span>
                <span style={styles.aiLiveBadge}>{t(LangKeys.help_ai_live_badge) || '24x7 LIVE'}</span>
              </div>
              <div style={styles.aiOnlineStatus}>
                {t(LangKeys.help_ai_online_status) || '• 24x7 त्वरित सहायता'}
              </div>
            </div>
          </div>

          <p style={styles.aiDesc}>
            {t(LangKeys.help_ai_desc) || 'खाता, बिलिंग, इन्वेंटरी, डेयरी पासबुक या प्लान से जुड़े सभी सवालों का तत्काल AI समाधान पाएं।'}
          </p>

          <button style={styles.aiActionBtn}>
            <Sparkles size={16} color="#10B981" />
            <span>{t(LangKeys.help_ai_chat_btn) || 'Chat with AI Support (24x7 Live)'}</span>
          </button>
        </div>

        {/* 3. OFFICIAL WHATSAPP SUPPORT CARD */}
        <div style={styles.whatsappCard} onClick={handleLaunchWhatsApp}>
          <div style={styles.whatsappLeft}>
            <div style={styles.whatsappIconBox}>
              <svg width="24" height="24" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <path fill="none" stroke="#15803D" strokeWidth="7" d="M83.4,16.6C74.5,7.7,62.9,2.5,50,2.5C26.5,2.5,7.5,21.5,7.5,45c0,7.9,2,15.4,5.7,21.8l-6.6,24.2l24.9-6.5 c6.2,3.4,13.2,5.4,20.6,5.4h0c23.5,0,42.5-19,42.5-42.5C92.5,30.6,89.2,22.7,83.4,16.6z"/>
                <path fill="#15803D" d="M68.2,58.3c-1-0.5-5.9-2.9-6.8-3.2c-0.9-0.3-1.6-0.5-2.2,0.5c-0.6,1-2.6,3.2-3.2,3.8 c-0.6,0.6-1.2,0.7-2.2,0.2c-1-0.5-4.2-1.5-8-4.9c-3-2.6-5-5.8-5.8-6.8c-0.8-1-0.1-1.5,0.4-2c0.5-0.5,1.1-1.3,1.7-1.9 c0.6-0.6,0.8-1,1.2-1.7c0.3-0.6,0.2-1.2-0.1-1.7c-0.3-0.5-2.2-5.3-2.9-7.2c-0.8-1.9-1.6-1.6-2.2-1.6h-1.4c-0.6,0-1.6,0.2-2.5,1.2 c-0.9,1-3.6,3.5-3.6,8.6c0,5.1,3.7,10,4.2,10.7c0.5,0.7,7.3,11.2,18,15.9c2.6,1.1,4.4,1.7,5.8,2.1c2.2,0.6,4.3,0.5,5.9-0.3 c1.8-0.9,5.9-2.4,6.7-4.8c0.8-2.4,0.8-4.4,0.6-4.8C70.3,58.8,69.2,58.7,68.2,58.3z"/>
              </svg>
            </div>
            <div>
              <div style={styles.whatsappTitle}>
                {t(LangKeys.help_whatsapp_title) || 'Contact Us (WhatsApp)'}
              </div>
              <div style={styles.whatsappSub}>
                {t(LangKeys.help_whatsapp_sub) || '+91 99293 71541 • आधिकारिक सपोर्ट टीम'}
              </div>
            </div>
          </div>
          <ChevronRight size={20} color="#16A34A" />
        </div>

        {/* 4. CALL HELPLINE & EMAIL SUPPORT (2-Column Grid) */}
        <div style={styles.channelGrid}>
          {/* Calling Helpline */}
          <div style={styles.channelCard} onClick={handleLaunchCall}>
            <div style={{ ...styles.channelIconBox, backgroundColor: '#EFF6FF', color: '#2563EB' }}>
              <Phone size={20} />
            </div>
            <div style={styles.channelTitle}>
              {t(LangKeys.help_call_title) || 'कॉल हेल्पलाइन'}
            </div>
            <div style={styles.channelSub}>
              {t(LangKeys.help_call_number) || '+91 9166277283'}
            </div>
          </div>

          {/* Email Support */}
          <div style={styles.channelCard} onClick={handleLaunchEmail}>
            <div style={{ ...styles.channelIconBox, backgroundColor: '#F5F3FF', color: '#7C3AED' }}>
              <Mail size={20} />
            </div>
            <div style={styles.channelTitle}>
              {t(LangKeys.help_email_title) || 'ईमेल सपोर्ट'}
            </div>
            <div style={styles.channelSub}>
              {t(LangKeys.help_email_id) || 'support@moxpal.com'}
            </div>
          </div>
        </div>

        {/* 5. QUICK HELP & FAQ ACCORDION */}
        <div style={styles.faqCard}>
          <div style={styles.faqHeader}>
            <HelpCircle size={20} color="#1D4ED8" />
            <span style={styles.faqHeading}>
              {t(LangKeys.help_faq_title) || 'अक्सर पूछे जाने वाले सवाल (FAQs)'}
            </span>
          </div>

          <div style={styles.faqList}>
            {faqs.map((faq, index) => {
              const isExpanded = expandedFaq === faq.id;

              return (
                <div key={faq.id} style={styles.faqItem}>
                  <button
                    onClick={() => setExpandedFaq(isExpanded ? null : faq.id)}
                    style={styles.faqQuestionBtn}
                  >
                    <span style={styles.faqQuestionText}>{faq.q}</span>
                    <ChevronDown
                      size={18}
                      color="#64748B"
                      style={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0
                      }}
                    />
                  </button>
                  {isExpanded && (
                    <div style={styles.faqAnswerBox}>
                      <p style={styles.faqAnswerText}>{faq.a}</p>
                    </div>
                  )}
                  {index !== faqs.length - 1 && <div style={styles.faqDivider} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* 6. SUPPORT HOURS & TRUST FOOTER */}
        <div style={styles.footerCard}>
          <div style={styles.footerRow}>
            <Clock size={18} color="#64748B" />
            <span style={styles.footerText}>
              {t(LangKeys.help_footer_call_hours) || 'कॉल सपोर्ट: सोम - शनि (सुबह 9:00 से रात 8:00)'}
            </span>
          </div>
          <div style={styles.footerRow}>
            <ShieldCheck size={18} color="#059669" />
            <span style={{ ...styles.footerText, color: '#059669' }}>
              {t(LangKeys.help_footer_ai_hours) || 'AI चैट सहायक: 24 घंटे 365 दिन तत्काल सहायता'}
            </span>
          </div>
          <div style={styles.footerDivider} />
          <div style={styles.footerBranding}>
            {t(LangKeys.help_footer_branding) || 'Moxpal Technologies • 100% सुरक्षित एवं विश्वसनीय • Made in India 🇮🇳'}
          </div>
        </div>
      </div>

      {/* AI SUPPORT CHAT MODAL */}
      <AiSupportChatModal
        isOpen={isAiChatOpen}
        onClose={() => setIsAiChatOpen(false)}
        userProfile={userProfile}
      />
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#F8FAFC',
    overflowY: 'auto'
  },
  banner: {
    background: 'linear-gradient(135deg, #0F172A, #1E293B)',
    padding: '32px 20px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
  },
  bannerIconWrapper: {
    width: '68px',
    height: '68px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1.5px solid rgba(255, 255, 255, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
    boxSizing: 'border-box'
  },
  bannerIconInner: {
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    boxSizing: 'border-box'
  },
  bannerTitle: {
    fontSize: '22px',
    fontWeight: '800',
    color: '#FFFFFF',
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em'
  },
  bannerSubtitle: {
    fontSize: '13px',
    color: '#CBD5E1',
    margin: 0,
    maxWidth: '520px',
    lineHeight: '1.45'
  },
  content: {
    padding: '20px',
    maxWidth: '820px',
    margin: '0 auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxSizing: 'border-box'
  },
  aiCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    border: '1.5px solid rgba(16, 185, 129, 0.35)',
    boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
    padding: '18px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  aiCardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  aiLogoBox: {
    width: '46px',
    height: '46px',
    borderRadius: '14px',
    backgroundColor: '#ECFDF5',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  aiTextCol: {
    flex: 1
  },
  aiTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  aiTitle: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#0F172A'
  },
  aiLiveBadge: {
    backgroundColor: '#059669',
    color: '#FFFFFF',
    fontSize: '9px',
    fontWeight: '800',
    padding: '2px 7px',
    borderRadius: '10px'
  },
  aiOnlineStatus: {
    fontSize: '12px',
    color: '#059669',
    fontWeight: '700',
    marginTop: '2px'
  },
  aiDesc: {
    fontSize: '12.5px',
    color: '#64748B',
    lineHeight: '1.45',
    margin: '12px 0 14px 0'
  },
  aiActionBtn: {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#0F172A',
    color: '#FFFFFF',
    fontSize: '13px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    transition: 'background 0.15s ease'
  },
  whatsappCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '18px',
    border: '1.2px solid rgba(22, 163, 74, 0.3)',
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.04)',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  whatsappLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  whatsappIconBox: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: '#DCFCE7',
    border: '1px solid #86EFAC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  whatsappTitle: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#0F172A'
  },
  whatsappSub: {
    fontSize: '12px',
    color: '#64748B',
    marginTop: '2px'
  },
  channelGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '14px'
  },
  channelCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '18px',
    border: '1px solid #E2E8F0',
    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
    padding: '16px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.15s ease'
  },
  channelIconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '10px'
  },
  channelTitle: {
    fontSize: '13.5px',
    fontWeight: '800',
    color: '#0F172A'
  },
  channelSub: {
    fontSize: '11.5px',
    color: '#64748B',
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  faqCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '18px',
    border: '1px solid #E2E8F0',
    boxShadow: '0 4px 14px rgba(0,0,0,0.03)',
    padding: '18px'
  },
  faqHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '14px'
  },
  faqHeading: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#0F172A'
  },
  faqList: {
    display: 'flex',
    flexDirection: 'column'
  },
  faqItem: {
    display: 'flex',
    flexDirection: 'column'
  },
  faqQuestionBtn: {
    width: '100%',
    border: 'none',
    backgroundColor: 'transparent',
    padding: '12px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'left',
    cursor: 'pointer',
    gap: '12px'
  },
  faqQuestionText: {
    fontSize: '13.5px',
    fontWeight: '700',
    color: '#1E293B'
  },
  faqAnswerBox: {
    padding: '0 0 12px 0'
  },
  faqAnswerText: {
    fontSize: '12.5px',
    color: '#64748B',
    lineHeight: '1.5',
    margin: 0
  },
  faqDivider: {
    height: '1px',
    backgroundColor: '#F1F5F9'
  },
  footerCard: {
    backgroundColor: '#F1F5F9',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  footerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  footerText: {
    fontSize: '12px',
    color: '#475569',
    fontWeight: '600'
  },
  footerDivider: {
    height: '1px',
    backgroundColor: '#E2E8F0',
    margin: '4px 0'
  },
  footerBranding: {
    fontSize: '11px',
    color: '#94A3B8',
    textAlign: 'center',
    fontWeight: '500'
  }
};

export default HelpSupportHome;
